/**
 * Loads still images as premultiplied `ImageBitmap`s, keyed per media input (URL or Blob, see
 * `inputKey`), so a replaced file or derivative is picked up automatically. Failed loads are
 * remembered until `clearFailures()`.
 */
import type { MediaInput } from '../types.ts';
import { LruCache } from '../util/lru.ts';
import { decodeImage } from './heif.ts';
import { inputBlob, inputKey } from './input.ts';

export interface LoadedImage {
  bitmap: ImageBitmap;
  width: number;
  height: number;
  /** `inputKey` of the source. */
  key: string;
}

export class ImageCache {
  private images = new LruCache<string, LoadedImage>(48, (_k, v) => v.bitmap.close());
  private pending = new Map<string, Promise<LoadedImage>>();
  private failed = new Set<string>();

  /** `onLoad` receives the input key of each image that finished loading. */
  constructor(private readonly onLoad?: (key: string) => void) {}

  /** Returns the image if loaded; otherwise starts loading it and returns null. */
  peek(input: MediaInput): LoadedImage | null {
    const key = inputKey(input);
    const hit = this.images.get(key);
    if (hit) return hit;
    if (!this.failed.has(key) && !this.pending.has(key)) void this.load(input).catch(() => undefined);
    return null;
  }

  load(input: MediaInput): Promise<LoadedImage> {
    const key = inputKey(input);
    const hit = this.images.get(key);
    if (hit) return Promise.resolve(hit);
    const inflight = this.pending.get(key);
    if (inflight) return inflight;
    const p = (async () => {
      try {
        const blob = await inputBlob(input);
        const bitmap = await decodeImage(blob, {
          imageOrientation: 'from-image',
          premultiplyAlpha: 'premultiply',
          colorSpaceConversion: 'default',
        });
        const img = { bitmap, width: bitmap.width, height: bitmap.height, key };
        this.images.set(key, img);
        this.onLoad?.(key);
        return img;
      } catch (err) {
        this.failed.add(key);
        throw err;
      } finally {
        this.pending.delete(key);
      }
    })();
    this.pending.set(key, p);
    return p;
  }

  clearFailures(): void {
    this.failed.clear();
  }

  clear(): void {
    this.images.clear();
    this.failed.clear();
  }
}
